import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  UserIcon,
  AcademicCapIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

import { Alert, Loading, Tabs } from '@/components/ui/daisy';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import UiVersionSwitcher from '@/components/ui/custom/UiVersionSwitcher';
import MaterialContainer from '@/components/admin/lessons/material/MaterialContainer';
import { lessonsQueryKeys } from '@/data/services/querykeys';
import Lesson from '@/models/Lesson';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import MaterialAddSection from './MaterialAddSection';

type Props = {
  lesson: Lesson;
  unitId: number;
  onLessonReplaced: (lessonId: number) => Promise<void>;
};

enum MaterialSubTab {
  STUDENT = 'STUDENT',
  TEACHER = 'TEACHER',
}

export default function MaterialsTabContent({
  lesson,
  unitId,
  onLessonReplaced,
}: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonForm.workspace.tabs.materialsSubTabs',
  });

  const { coursePath, isLoading } = usePedWorkspace();
  const { tab: subTab, setTab: setSubTab } = useWorkspaceTabs<MaterialSubTab>({
    defaultTab: MaterialSubTab.STUDENT,
    tabParam: 'material-tab',
  });
  const queryClient = useQueryClient();

  const invalidateLesson = async (lessonId?: number) => {
    if (lessonId && lessonId !== lesson.id) return onLessonReplaced(lessonId);
    await queryClient.invalidateQueries({
      queryKey: lessonsQueryKeys.get(lesson.id).queryKey,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  if (!coursePath) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="soft" className="rounded-2xl">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
          <span className="text-sm">{t('noCoursePathFallback')}</span>
        </Alert>
      </div>
    );
  }

  return (
    <>
      <Tabs
        variant="box"
        className="w-fit"
        value={subTab}
        onChange={value => setSubTab(value as MaterialSubTab)}
      >
        <Tabs.Tab value={MaterialSubTab.STUDENT} className="flex gap-2">
          <UserIcon className="size-3.5" />
          {t('student')}
        </Tabs.Tab>
        <Tabs.Tab value={MaterialSubTab.TEACHER} className="flex gap-2">
          <AcademicCapIcon className="size-3.5" />
          {t('teacher')}
        </Tabs.Tab>
      </Tabs>
      <UiVersionSwitcher scope="lessonMaterials">
        <UiVersionSwitcher.Legacy>
          <ConditionalRenderer condition={subTab === MaterialSubTab.STUDENT}>
            <div className="bg-base-200 p-4 rounded-2xl">
              <MaterialContainer
                key={lesson.book?.id}
                lesson={lesson}
                course={coursePath}
                blockId={unitId}
                bookType="STUDENT"
                updateLessons={invalidateLesson}
              />
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer condition={subTab === MaterialSubTab.TEACHER}>
            <div className="bg-base-200 p-4 rounded-2xl">
              <MaterialContainer
                key={lesson.teacherBook}
                lesson={lesson}
                course={coursePath}
                blockId={unitId}
                bookType="TEACHER"
                updateLessons={invalidateLesson}
              />
            </div>
          </ConditionalRenderer>
        </UiVersionSwitcher.Legacy>
        <UiVersionSwitcher.Modern>
          <MaterialAddSection key={subTab} bookType={subTab} />
        </UiVersionSwitcher.Modern>
      </UiVersionSwitcher>
    </>
  );
}
